import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";

interface Props {
  Menuprops: menuInfo;
}

// 그룹등록
const SN_MAS_ORG_REG_005 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
  });
  const [formDatas, setFormDatas] = useState<any>({
    freeform1: null,
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
        setFormControls({
          freeform1: rtn.form.freeform1,
        });
        setFormDatas({
          freeform1: null,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirmCancel",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const isSaved: any = await onSave();
          if (!isSaved) return;
        } else if (confirm === undefined) {
          return;
        }
      }

      const params = {
        id: "sn_mas_org_reg_005_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(params);
      setFormDatas({
        freeform1: null,
      });
      setDataSources({
        grid1: data[0] || [],
      });

      gridRefs.current["grid1"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = () => {
    gridRefs.current["grid1"].current?.addRow();
  };

  const onBeforeAddRow = (): any => {
    try {
      const formData = searchRef.current.getFormdata();
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: formData.cd_biz,
        cd_cc: "",
        yn_use: "Y",
        no_seq: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid1"].current?.deleteSelectedRows();

    if (gridRefs.current["grid1"].current.getDisplayedRowCount() < 1) {
      setFormDatas({
        freeform1: null,
      });
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const requiredValuesCheck =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheck) return false;

      const groupChanges = gridRefs.current["grid1"].current.getChange();
      if (groupChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const hasInvalidCdgp = [
        ...(groupChanges.insert || []),
        ...(groupChanges.update || []),
      ].some((item) => item.cd_gp && item.cd_gp.length > 6);
      if (hasInvalidCdgp) {
        await showDialog("alter", "그룹코드가 6자리를 초과하였습니다.");
        return false;
      }

      const hasInvalidNmgp = [
        ...(groupChanges.insert || []),
        ...(groupChanges.update || []),
      ].some((item) => item.nm_gp && item.nm_gp.length > 30);
      if (hasInvalidNmgp) {
        await showDialog("alter", "그룹명이 30자리를 초과하였습니다.");
        return false;
      }

      const saveData = {
        gp: groupChanges,
      };
      const ok = await fnSave("SN_MAS_ORG_REG_005_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error) {}
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      setFormDatas({
        freeform1: selectedRow,
      });
    }
  };

  const onCellValueChanged = (event: any) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    console.log(colId, rowData, rowNode);
  };

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        etcProps: {},
        events: {},
      },
    }),
    [formControls, formDatas]
  );

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onBeforeAddRow: onBeforeAddRow,
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "그룹정보" }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel1" orgSize="49.5%">
          <SnForm
            ref={formRefs.current["freeform1"]}
            gridref={gridRefs.current["grid1"]}
            data={forms.freeform1.data}
            controls={forms.freeform1.controls}
            events={forms.freeform1.events}
            etcProps={{ title: "상세내용" }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MAS_ORG_REG_005;
